import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

export interface ApiError {
  success: false;
  statusCode: number;
  message: string;
  errors?: string[];
  // Validation failures keyed by DTO property (e.g. "gstNumber", "items.0.rate")
  fieldErrors?: Record<string, string>;
  timestamp: string;
}

// Must stay root-relative ('/api', not '../api') — see the warehouse project's
// note: a directory-relative base breaks on nested routes like /billing/invoices/3.
const API_BASE_URL =
  process.env.REACT_APP_API_URL ??
  (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3002/api');

const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
  headers: { 'Content-Type': 'application/json', 'Cache-Control': 'no-cache' },
});

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem('authToken');
  if (token && config.headers) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Blob downloads come back as Blob even on error; turn the JSON error body
// back into a message so toasts stay readable.
async function readBlobError(data: unknown): Promise<ApiError | null> {
  if (data instanceof Blob && data.type.includes('json')) {
    try {
      return JSON.parse(await data.text());
    } catch {
      return null;
    }
  }
  return null;
}

apiClient.interceptors.response.use(
  // Unwrap the { success, data, message } envelope's outer axios layer.
  (response) => response.data,
  async (error: AxiosError<ApiError>) => {
    if (error.response) {
      const body = (await readBlobError(error.response.data)) ?? error.response.data;
      const isLogin = error.config?.url?.includes('/auth/login');

      if (error.response.status === 401 && !isLogin && localStorage.getItem('authToken')) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }

      let message = body?.message || 'Request failed';
      if (error.response.status === 403 && (!body?.message || body.message.startsWith('You do not have permission'))) {
        message = "Access denied: your role doesn't allow this action.";
      }
      // Surface the first validation message instead of a generic one.
      if (body?.errors?.length) message = String(body.errors[0]);

      return Promise.reject({ ...body, message } as ApiError);
    }
    return Promise.reject({
      success: false,
      statusCode: 0,
      message: error.request ? 'Network error — is the server running?' : error.message,
      timestamp: new Date().toISOString(),
    } as ApiError);
  },
);

// Every endpoint returns { success, message, data }; these helpers return `data`.
export const api = {
  get: async <T = any>(url: string, params?: Record<string, unknown>): Promise<T> =>
    ((await apiClient.get(url, { params })) as any).data,
  post: async <T = any>(url: string, body?: unknown): Promise<T> => ((await apiClient.post(url, body)) as any).data,
  put: async <T = any>(url: string, body?: unknown): Promise<T> => ((await apiClient.put(url, body)) as any).data,
  patch: async <T = any>(url: string, body?: unknown): Promise<T> => ((await apiClient.patch(url, body)) as any).data,
  delete: async <T = any>(url: string, params?: Record<string, unknown>): Promise<T> =>
    ((await apiClient.delete(url, { params })) as any).data,
  download: async (url: string, params?: Record<string, unknown>): Promise<Blob> =>
    (await apiClient.get(url, { params, responseType: 'blob' })) as unknown as Blob,
};

export function triggerBlobDownload(blob: Blob, filename: string): void {
  const href = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(href);
}

export function errorMessage(err: unknown, fallback = 'Something went wrong'): string {
  return (err as ApiError)?.message || fallback;
}

export default apiClient;
